import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import { useModal } from "../../ui/modal/ModalProvider";
import { MOCK_DATA } from "../../../data/mockDataStore";
import { relative } from "../../../utils/format";

function initialsOf(name) {
  return (name || "")
    .split(/[\s.@]+/)
    .filter(Boolean)
    .map((x) => x[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function Topbar({ onMenuToggle }) {
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const { openModal, closeModal } = useModal();
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);

  const token = keycloak?.tokenParsed || {};
  const displayName = token.preferred_username || token.email || "Utilisateur";
  const roleLabel = token.type || "Agent";
  const email = token.email;

  useEffect(() => {
    if (!accountMenuOpen) return;
    const onClickOutside = (e) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) {
        setAccountMenuOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setAccountMenuOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [accountMenuOpen]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleSearchKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const q = query.trim();
    if (!q) return;
    if (q.startsWith("TK-") || q.toLowerCase().includes("ticket")) navigate("/tickets?q=" + encodeURIComponent(q));
    else if (q.startsWith("R-") || q.startsWith("V-")) navigate("/reservations?q=" + encodeURIComponent(q));
    else if (q.startsWith("C-")) navigate("/circuits?q=" + encodeURIComponent(q));
    else if (q.startsWith("E-")) navigate("/etablissements?q=" + encodeURIComponent(q));
    else navigate("/tickets?q=" + encodeURIComponent(q));
  };

  const openNotifications = () => {
    const items = MOCK_DATA.notifications.map((n, i) => (
      <div
        key={i}
        style={{
          padding: "10px 12px",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          marginBottom: 8,
          background: "var(--surface-2)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className={`badge ${n.type === "danger" ? "danger" : n.type === "warn" ? "warning" : "info"}`}>
            <i className="fa-solid fa-bell"></i>
          </span>
          <strong style={{ fontSize: 13 }}>{n.titre}</strong>
          <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--text-3)" }}>{relative(n.date)}</span>
        </div>
        <div style={{ color: "var(--text-2)", fontSize: 12.5, marginTop: 4 }}>{n.texte}</div>
      </div>
    ));
    openModal({
      title: "Notifications",
      body: items,
      footer: (
        <button className="btn" onClick={closeModal}>
          Fermer
        </button>
      ),
    });
  };

  const openQuickCreate = () => {
    const go = (path) => {
      closeModal();
      navigate(path);
    };
    openModal({
      title: "Créer un élément",
      body: (
        <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div className="action-shortcut" onClick={() => go("/tickets?new=1")} data-testid="qc-ticket">
            <div className="ico">
              <i className="fa-solid fa-life-ring"></i>
            </div>
            <div>
              <div className="lbl">Nouveau ticket</div>
              <div className="sub">Créer un ticket support</div>
            </div>
          </div>
          <div className="action-shortcut" onClick={() => go("/academie?new=1")} data-testid="qc-formation">
            <div className="ico">
              <i className="fa-solid fa-video"></i>
            </div>
            <div>
              <div className="lbl">Nouvelle formation</div>
              <div className="sub">Ajouter une vidéo Académie</div>
            </div>
          </div>
          <div className="action-shortcut" onClick={() => go("/etablissements")} data-testid="qc-etab">
            <div className="ico">
              <i className="fa-solid fa-building"></i>
            </div>
            <div>
              <div className="lbl">Établissements</div>
              <div className="sub">Voir les demandes en attente</div>
            </div>
          </div>
          <div className="action-shortcut" onClick={() => go("/reservations?f=refund")} data-testid="qc-refund">
            <div className="ico">
              <i className="fa-solid fa-rotate-left"></i>
            </div>
            <div>
              <div className="lbl">Remboursements</div>
              <div className="sub">Traiter les demandes</div>
            </div>
          </div>
        </div>
      ),
    });
  };

  return (
    <header className="topbar">
      <button className="icon-btn menu-toggle" id="menuToggle" data-testid="menu-toggle" aria-label="Ouvrir le menu" onClick={onMenuToggle}>
        <i className="fa-solid fa-bars"></i>
      </button>
      <div className="topbar-search">
        <i className="fa-solid fa-magnifying-glass"></i>
        <input
          ref={searchRef}
          type="search"
          id="globalSearch"
          data-testid="global-search"
          placeholder="Rechercher un ticket, établissement, réservation…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleSearchKeyDown}
        />
        <kbd>⌘K</kbd>
      </div>
      <div className="topbar-actions">
        <button className="icon-btn" data-testid="btn-help" title="Aide">
          <i className="fa-regular fa-circle-question"></i>
        </button>
        <button className="icon-btn notif-btn" id="notifBtn" data-testid="btn-notifications" title="Notifications" onClick={openNotifications}>
          <i className="fa-regular fa-bell"></i>
          <span className="dot"></span>
        </button>
        <button className="btn btn-primary" id="quickCreateBtn" data-testid="btn-quick-create" onClick={openQuickCreate}>
          <i className="fa-solid fa-plus"></i> Nouveau
        </button>
        <div className="account-menu-wrap" ref={accountMenuRef}>
          <div
            className="agent-card"
            data-testid="topbar-account"
            onClick={() => setAccountMenuOpen((v) => !v)}
            style={{ cursor: "pointer" }}
          >
            <div className="agent-avatar">{initialsOf(displayName) || "?"}</div>
            <div className="agent-meta">
              <div className="agent-name">{displayName}</div>
              <div className="agent-role">{roleLabel}</div>
            </div>
            <i className="fa-solid fa-ellipsis-vertical agent-more"></i>
          </div>
          {accountMenuOpen ? (
            <div className="account-menu" data-testid="account-menu">
              <div className="account-menu-header">
                <div className="agent-name">{displayName}</div>
                {email ? <div className="agent-role">{email}</div> : null}
              </div>
              <button className="account-menu-item danger" data-testid="btn-logout" onClick={() => keycloak?.logout()}>
                <i className="fa-solid fa-right-from-bracket"></i> Se déconnecter
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}

export default Topbar;
